import { ConditionOperatorEnumType, CountOperator } from '@reach/shared-types/conditions';
import { generateId } from '@/lib/generate-id';
import { ChevronDown, ChevronRight, Link2, Trash2 } from 'lucide-react';
import React from 'react';
import { AddDescriptionButton } from './AddDescriptionButton';
import { ClauseDescriptionHeadline } from './ClauseDescriptionHeadline';
import { FieldConditionRow } from './FieldConditionRow';
import { useSegmentSchemaCatalog } from './useSegmentSchemaCatalog';
import type { BuilderFieldCondition, BuilderRelatedResourceCondition } from './segment-builder.types';
import styles from './SegmentVisualBuilder.module.css';

const COUNT_OPERATOR_OPTIONS: { value: CountOperator; label: string }[] = [
  { value: CountOperator.EQ, label: 'exactly' },
  { value: CountOperator.GT, label: 'more than' },
  { value: CountOperator.GTE, label: 'at least' },
  { value: CountOperator.LT, label: 'fewer than' },
  { value: CountOperator.LTE, label: 'at most' },
];

interface RelatedResourceBlockProps {
  condition: BuilderRelatedResourceCondition;
  onUpdate: (condition: BuilderRelatedResourceCondition) => void;
  onDelete: () => void;
  disabled?: boolean;
}

export const RelatedResourceBlock: React.FC<RelatedResourceBlockProps> = ({
  condition,
  onUpdate,
  onDelete,
  disabled = false,
}) => {
  const { getSchemaById } = useSegmentSchemaCatalog();
  const schema = getSchemaById(condition.schemaId);
  const fieldOptions = schema?.fields ?? [];
  const childConditions = condition.conditions;
  const isExpanded = condition.isExpanded;
  const isReadOnly = disabled || condition.unavailableReason !== undefined;

  const toggleExpanded = () => {
    onUpdate({ ...condition, isExpanded: !condition.isExpanded });
  };

  const handleCountOperatorChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onUpdate({
      ...condition,
      countOperator: e.target.value as CountOperator,
    });
  };

  const handleCountValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = parseInt(e.target.value, 10);
    if (!Number.isNaN(v) && v >= 0) {
      onUpdate({ ...condition, countValue: v });
    }
  };

  const handleNestedConditionUpdate = (index: number, updated: BuilderFieldCondition) => {
    onUpdate({ ...condition, conditions: childConditions.with(index, updated) });
  };

  const handleNestedConditionDelete = (index: number) => {
    onUpdate({ ...condition, conditions: childConditions.toSpliced(index, 1) });
  };

  const handleDescriptionChange = (description: string | undefined) => {
    onUpdate({ ...condition, description });
  };

  const handleAddNestedCondition = () => {
    const newCond: BuilderFieldCondition = {
      id: generateId(),
      type: 'field',
      field: fieldOptions[0]?.name ?? '',
      fieldType: fieldOptions[0]?.type ?? 'unknown',
      operator: ConditionOperatorEnumType.EQUALS,
      value: [],
    };
    onUpdate({
      ...condition,
      conditions: [...condition.conditions, newCond],
    });
  };

  return (
    <div className={styles.relatedBlock}>
      <div className={styles.relatedHeader}>
        <div className={styles.relatedTitle}>
          <button
            type="button"
            className={styles.relatedTitleButton}
            onClick={toggleExpanded}
            disabled={isReadOnly}
            aria-expanded={isExpanded}
            aria-label={isExpanded ? 'Collapse' : 'Expand'}>
            {isExpanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
          </button>
          <Link2 size={14} style={{ color: '#6b7280' }} />
          <span>
            {condition.schemaName} — via {condition.foreignKeyPath}
          </span>
        </div>
        <div className={styles.nodeHeaderControls}>
          {condition.description === undefined && (
            <AddDescriptionButton onClick={() => handleDescriptionChange('')} disabled={isReadOnly} />
          )}
          <button
            type="button"
            className={styles.relatedTitleButton}
            onClick={onDelete}
            disabled={isReadOnly}
            aria-label="Remove related resource condition">
            <Trash2 size={16} />
          </button>
        </div>
      </div>

      {condition.description !== undefined && (
        <div className={styles.relatedDescription}>
          <ClauseDescriptionHeadline
            variant="row"
            description={condition.description}
            onChange={handleDescriptionChange}
            disabled={isReadOnly}
          />
        </div>
      )}

      {condition.unavailableReason && (
        <p className={styles.unavailableMessage}>{condition.unavailableReason}</p>
      )}

      <div className={styles.relatedCountRow}>
        <span>Contact has</span>
        <select
          className={styles.selectInput}
          value={condition.countOperator}
          onChange={handleCountOperatorChange}
          disabled={isReadOnly}
          style={{ width: 'auto' }}
          aria-label="Count operator">
          {COUNT_OPERATOR_OPTIONS.map(opt => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        <input
          type="number"
          min={0}
          value={condition.countValue}
          onChange={handleCountValueChange}
          disabled={isReadOnly}
          className={styles.textInput}
          style={{ width: '4rem' }}
          aria-label="Count value"
        />
        <span>matching {condition.schemaName}</span>
      </div>

      {isExpanded && (
        <div className={styles.relatedNested}>
          <div className={styles.relatedNestedLabel}>WHERE (all must match)</div>
          {condition.conditions.map((c, i) => (
            <FieldConditionRow
              key={c.id}
              condition={c}
              fieldOptions={fieldOptions}
              onUpdate={handleNestedConditionUpdate.bind(null, i)}
              onDelete={handleNestedConditionDelete.bind(null, i)}
              disabled={isReadOnly}
            />
          ))}
          <div className={styles.addConditionRow}>
            <button
              type="button"
              className={styles.addConditionBtn}
              onClick={handleAddNestedCondition}
              disabled={isReadOnly || fieldOptions.length === 0}>
              + Add condition
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
